import { useEffect, useId, useMemo, useRef, useState, type ClipboardEvent, type DragEvent } from 'react';
import type { ChatMessage, GeneratedImageArtifact } from '@shipping/core';
import AssistantMessage from '../chat/AssistantMessage';
import ImageGenerationModePicker from '../chat/ImageGenerationModePicker';
import ImageRatioPicker from '../chat/ImageRatioPicker';
import TypingIndicator from '../chat/TypingIndicator';
import { enhancePrompt } from '../../lib/chat-api';
import { generatedImageDownloadUrl } from '../../lib/generated-image-download';
import { imageTransferPayload, isOwnedImageReferenceUrl, loadImageReferenceFile, normalizeImageFile, resolveImageTransfer, type ImageTransferPayload } from '../../lib/image-input-transfer';
import ImageAttachmentPreview from '../chat/ImageAttachmentPreview';
import { useWebConsumerAuthStore } from '../../stores/useWebConsumerAuthStore';
import { imageDimensionsError, imageRoute } from '../../lib/image-capabilities';
import { imageCanvasProgressLabel, type ImageAspectRatio, type ImageCanvasProgress, type ImageCapabilities, type ImageGenerationMode } from '../../lib/image-generation-api';
import {
  imageModelGenerationMode,
  imageModelSelectionError,
  imageModelRequestOptions,
  normalizeImageModelSelection,
  normalizeImageModelId,
  imageModelQualityLabel,
  type ImageModelId,
  type ImageModelOption,
} from '../../lib/image-model-catalog';
import '../../pages/AiChatPage.css';
import './DesignStudio.css';

export type DesignStudioSkillKind = 'image' | 'text' | 'mixed' | 'document';

export interface DesignStudioPrompt {
  id: string;
  title: string;
  text: string;
  requiresRightsConfirmation?: boolean;
  requiresNonOfficialTemplate?: boolean;
}

export interface DesignStudioSkill {
  id: string;
  title: string;
  kind: DesignStudioSkillKind;
  prompts: DesignStudioPrompt[];
  referenceMin?: number;
  referenceHint?: string;
  requiresRightsConfirmation?: boolean;
  requiresNonOfficialTemplate?: boolean;
}

export interface DesignStudioCategory {
  id: string;
  title: string;
  skills: DesignStudioSkill[];
}

export interface DesignStudioCatalog {
  version: number;
  categories: DesignStudioCategory[];
}

export interface DesignStudioDraft {
  categoryId: string;
  skillId: string;
  kind: DesignStudioSkillKind;
  prompt: string;
  promptId: string | null;
  referenceFiles: File[];
  aspectRatio: ImageAspectRatio;
  generationMode: ImageGenerationMode;
  imageModel?: ImageModelId;
  resolution?: string | null;
  quality?: string | null;
  customDimensions: string;
  outputWidth?: number;
  outputHeight?: number;
  transparentBackground?: boolean | null;
  rightsConfirmed: boolean;
  nonOfficialTemplate: boolean;
}

export interface DesignStudioQuote {
  quoteId: string;
  minCredits: string;
  maxCredits: string;
  assetCount: number;
  description?: string;
  availableCredits?: string;
  insufficientCredit?: boolean;
  plan?: {
    steps: { kind: string; title: string; description: string }[];
    outputFormat: 'docx' | 'png' | 'text+png' | 'text';
    itemCount: number;
  };
}

export interface DesignStudioResult {
  id: string;
  title: string;
  kind: DesignStudioSkillKind;
  status: 'queued' | 'running' | 'completed' | 'failed';
  previewUrls?: string[];
  images?: {
    imageId?: string;
    url: string;
    downloadUrl?: string;
    width?: number;
    height?: number;
    model?: string;
    originalImageId?: string;
    originalKind?: 'generation' | 'print';
    originalDownloadUrl?: string;
    originalWidth?: number;
    originalHeight?: number;
  }[];
  downloadUrl?: string;
  text?: string;
  error?: string;
  warning?: string;
  settledCreditMicros?: number;
  canvasProgress?: ImageCanvasProgress;
}

interface DesignStudioProps {
  catalog: DesignStudioCatalog;
  onSubmit: (draft: DesignStudioDraft) => Promise<string | void> | string | void;
  onReferenceFilesChange?: (files: File[]) => void;
  isSubmitting?: boolean;
  submitError?: string | null;
  results?: DesignStudioResult[];
  onRetry?: (id: string) => void;
  imageCapabilities?: ImageCapabilities | null;
  imageModels?: ImageModelOption[];
  imageModelsError?: string | null;
  restoredTurns?: DesignStudioConversationTurn[];
  restoredRun?: {
    id: string;
    prompt: string;
    imageModel?: ImageModelId | null;
    resolution?: string | null;
    quality?: string | null;
    aspectRatio?: ImageAspectRatio;
    generationMode?: ImageGenerationMode;
    customDimensions?: string;
    outputWidth?: number | null;
    outputHeight?: number | null;
    transparentBackground?: boolean | null;
  } | null;
}

interface DesignStudioConversationTurn {
  id: string;
  prompt: string;
  resultId: string | null;
}

interface GeneratedReferenceSource {
  id: string;
  url: string;
  downloadUrl?: string;
}

const MAX_REFERENCE_FILES = 10;
const FREEFORM_IMAGE_SKILL_ID = 'general-image-generation';
const FREEFORM_IMAGE_SKILL: DesignStudioSkill = {
  id: FREEFORM_IMAGE_SKILL_ID,
  title: '自由生图',
  kind: 'image',
  prompts: [],
};
const CATEGORY_ORDER = [
  'image-processing',
  'poster-design',
  'ecommerce-design',
  'brand-design',
  'social-media-marketing',
  'office-design',
  'apparel-wear',
] as const;

const categoryGlyphs: Record<string, string> = {
  'image-processing': '🪄',
  'poster-design': '🖼️',
  'ecommerce-design': '🛍️',
  'brand-design': '💎',
  'social-media-marketing': '📣',
  'office-design': '💼',
  'apparel-wear': '👕',
};

const skillGlyphs: Record<string, string> = {
  'office-design-resume-design': '📄',
  'office-design-id-photo': '🪪',
  'office-design-office-material': '🗂️',
  'office-design-chart-infographic': '📊',
  'office-design-manual-brochure': '📖',
  'office-design-picture-book': '📚',
  'social-media-marketing-note-cover': '📰',
  'social-media-marketing-viral-note-recreate': '🔥',
  'social-media-marketing-social-note-set': '🖼️',
  'social-media-marketing-brand-seller-social': '🏪',
  'social-media-marketing-custom-social-ip': '🌟',
  'social-media-marketing-ip-note-continuation': '✍️',
  'social-media-marketing-note-brief': '📝',
  'social-media-marketing-paid-knowledge-graphic': '🎓',
  'social-media-marketing-past-style-note': '📻',
  'social-media-marketing-personal-media-note': '👤',
  'social-media-marketing-social-analysis': '📈',
  'social-media-marketing-social-style-image': '🎨',
  'social-media-marketing-store-traffic-note': '📍',
  'poster-design-marketing-poster': '📣',
  'poster-design-poster-beautify': '✨',
  'poster-design-utility-poster': '📅',
  'ecommerce-design-listing-images': '🛍️',
  'ecommerce-design-white-bg': '💡',
  'ecommerce-design-detail-page': '🧩',
  'ecommerce-design-bestseller-replication': '🔥',
  'ecommerce-design-competitor-analysis': '📊',
  'ecommerce-design-keyword-traffic': '🔍',
  'ecommerce-design-listing-copy': '📝',
  'ecommerce-design-sku-variant': '🎛️',
  'brand-design-logo-design': '✒️',
  'brand-design-brand-ip': '🙂',
  'brand-design-packaging-design': '📦',
  'brand-design-product-design': '🧊',
  'brand-design-brand-extension': '🎨',
  'brand-design-brand-guide': '📕',
  'brand-design-sticker-pack': '🏷️',
  'apparel-wear-model-tryon': '🧍',
  'apparel-wear-model-swap': '🔁',
  'apparel-wear-apparel-variant': '👚',
  'apparel-wear-ugc-lifestyle': '📸',
  'apparel-wear-conversion-photo': '🎯',
  'apparel-wear-material-to-apparel': '🧵',
  'apparel-wear-nail-art': '💅',
  'image-processing-smart-crop': '✂️',
  'image-processing-content-removal': '🧽',
  'image-processing-batch-transform': '🗃️',
  'image-processing-product-replace': '🔄',
  'image-processing-image-translate': '🌐',
  'image-processing-text-edit': '🔤',
};

const fallbackSkillGlyphs: Record<DesignStudioSkillKind, string> = {
  image: '🖼️',
  text: '📝',
  mixed: '🎨',
  document: '📄',
};

function generatedReferenceSources(result: DesignStudioResult): GeneratedReferenceSource[] {
  if (result.images?.length) {
    return result.images.map((image, index) => ({
      id: image.imageId || `${result.id}-${index + 1}`,
      url: image.url,
      downloadUrl: image.downloadUrl,
    }));
  }
  return (result.previewUrls ?? []).map((url, index) => ({
    id: `${result.id}-${index + 1}`,
    url,
    downloadUrl: url,
  }));
}

function canonicalImageUrl(url: string): string {
  try {
    return new URL(url, window.location.href).href;
  } catch {
    return url.trim();
  }
}

function orderedCategories(categories: DesignStudioCategory[]): DesignStudioCategory[] {
  const rank = new Map<string, number>(CATEGORY_ORDER.map((id, index) => [id, index]));
  return categories
    .map((category, originalIndex) => ({ category, originalIndex }))
    .sort((left, right) => {
      const leftRank = rank.get(left.category.id) ?? CATEGORY_ORDER.length;
      const rightRank = rank.get(right.category.id) ?? CATEGORY_ORDER.length;
      return leftRank - rightRank || left.originalIndex - right.originalIndex;
    })
    .map(({ category }) => category);
}

function resultMessage(result: DesignStudioResult, prompt: string): ChatMessage {
  const completionCopy = result.kind === 'image'
    ? '已根据你的描述生成图片。'
    : '已完成本次创作。';
  const visibleText = [result.error?.trim() || result.text?.trim() || completionCopy, imageCanvasProgressLabel(result.canvasProgress), result.warning]
    .filter(Boolean).join('\n\n');
  const images: NonNullable<DesignStudioResult['images']> = result.images?.length
    ? result.images
    : (result.previewUrls ?? []).map((url) => ({
      imageId: undefined,
      url,
      downloadUrl: url,
      width: undefined,
      height: undefined,
      model: undefined,
    }));
  const artifacts = images.map<GeneratedImageArtifact>((image, index) => ({
    type: 'generated_image',
    image_id: image.imageId || `${result.id}-${index + 1}`,
    url: image.url,
    download_url: generatedImageDownloadUrl(image.imageId, image.downloadUrl, image.url),
    prompt: `${result.title} 第 ${index + 1} 张`,
    width: image.width ?? 0,
    height: image.height ?? 0,
    model: image.model ?? '',
    original_image_id: image.originalImageId,
    original_kind: image.originalKind,
    original_download_url: generatedImageDownloadUrl(image.originalImageId, image.originalDownloadUrl),
    original_width: image.originalWidth,
    original_height: image.originalHeight,
  }));
  const content = [visibleText, ...artifacts.map((artifact) => `<artifact>${JSON.stringify(artifact)}</artifact>`)]
    .join('\n');
  return {
    id: result.id,
    role: 'assistant',
    content,
    createdAt: new Date(0).toISOString(),
    metadata: {
      design_studio_prompt: prompt,
      ...(result.settledCreditMicros && result.settledCreditMicros > 0
        ? { metered_settled_credit_micros: result.settledCreditMicros }
        : {}),
    },
  };
}

export default function DesignStudio({
  catalog,
  onSubmit,
  onReferenceFilesChange,
  isSubmitting = false,
  submitError,
  results = [],
  onRetry,
  imageCapabilities,
  imageModels,
  imageModelsError,
  restoredRun = null,
  restoredTurns,
}: DesignStudioProps) {
  const categories = useMemo(() => orderedCategories(catalog.categories), [catalog.categories]);
  const [categoryId, setCategoryId] = useState(categories[0]?.id ?? '');
  const [skillId, setSkillId] = useState<string | null>(null);
  const [prompt, setPrompt] = useState('');
  const [promptId, setPromptId] = useState<string | null>(null);
  const [referenceFiles, setReferenceFiles] = useState<File[]>([]);
  const [aspectRatio, setAspectRatio] = useState<ImageAspectRatio>('auto');
  const [generationMode, setGenerationMode] = useState<ImageGenerationMode>('standard');
  const [imageModel, setImageModel] = useState<ImageModelId>('image-2.6-standard');
  const [modelParameters, setModelParameters] = useState<Partial<Record<ImageModelId, { resolution: string; quality: string }>>>({});
  const [customDimensions, setCustomDimensions] = useState('');
  const [rightsConfirmed, setRightsConfirmed] = useState(false);
  const [nonOfficialTemplate, setNonOfficialTemplate] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [isEnhancingPrompt, setIsEnhancingPrompt] = useState(false);
  const [isImportingReference, setIsImportingReference] = useState(false);
  const [promptEnhancementError, setPromptEnhancementError] = useState<string | null>(null);
  const [localError, setLocalError] = useState<string | null>(null);
  const [conversationTurns, setConversationTurns] = useState<DesignStudioConversationTurn[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const submitButtonRef = useRef<HTMLButtonElement>(null);
  const chatEndRef = useRef<HTMLDivElement>(null);
  const isMountedRef = useRef(true);
  const isImportingReferenceRef = useRef(false);
  const referenceFilesRef = useRef(referenceFiles);
  const latestPromptRef = useRef(prompt);
  const restoredRunIdRef = useRef(restoredRun?.id);
  restoredRunIdRef.current = restoredRun?.id;
  const tabIdPrefix = useId();
  latestPromptRef.current = prompt;
  referenceFilesRef.current = referenceFiles;

  const category = categories.find((item) => item.id === categoryId) ?? categories[0];
  const skill = category?.skills.find((item) => item.id === skillId);
  const effectiveSkill = skill ?? FREEFORM_IMAGE_SKILL;
  const selectedPrompt = skill?.prompts.find((item) => item.id === promptId);
  const requiresRightsConfirmation = Boolean(skill?.requiresRightsConfirmation || selectedPrompt?.requiresRightsConfirmation);
  const requiresNonOfficialTemplate = Boolean(skill?.requiresNonOfficialTemplate || selectedPrompt?.requiresNonOfficialTemplate);
  const busy = isSending || isSubmitting || isEnhancingPrompt || isImportingReference;
  const selectedImageModel = imageModels?.find((model) => model.id === imageModel);
  const savedParameters = modelParameters[imageModel] ?? {
    resolution: '1K', quality: 'auto',
  };
  const selection = selectedImageModel ? {
    ...normalizeImageModelSelection(selectedImageModel, { aspectRatio, ...savedParameters }), customDimensions,
  } : { aspectRatio, ...savedParameters, customDimensions };
  const selectedParameters = selection;
  const ratioRoute = imageCapabilities ? imageRoute(imageCapabilities, referenceFiles.length) : null;
  const ratioError = effectiveSkill.kind === 'text' ? '' : selectedImageModel
    ? imageModelSelectionError(selectedImageModel, selection, referenceFiles.length)
    : imageModels ? '图片模型尚未加载，请刷新页面重试。'
      : ratioRoute ? imageDimensionsError(ratioRoute, aspectRatio, customDimensions) : '';
  const missingRequiredReferences = referenceFiles.length < (skill?.referenceMin ?? 0);

  useEffect(() => {
    isMountedRef.current = true;
    return () => { isMountedRef.current = false; };
  }, []);

  useEffect(() => {
    if (!categories.some((item) => item.id === categoryId)) {
      setCategoryId(categories[0]?.id ?? '');
      setSkillId(null);
      setPrompt('');
      setPromptId(null);
    }
  }, [categories, categoryId]);

  useEffect(() => {
    if (!conversationTurns.length) return;
    chatEndRef.current?.scrollIntoView?.({ block: 'end', behavior: 'smooth' });
  }, [conversationTurns, results]);

  useEffect(() => {
    if (!restoredRun) return;
    const restoredModel = normalizeImageModelId(restoredRun.imageModel);
    if (restoredModel) {
      setImageModel(restoredModel);
      setGenerationMode(imageModelGenerationMode(restoredModel));
      setModelParameters((current) => ({
        ...current,
        [restoredModel]: {
          resolution: restoredRun.resolution ?? '1K',
          quality: restoredRun.quality ?? 'auto',
        },
      }));
    } else if (restoredRun.generationMode) {
      setGenerationMode(restoredRun.generationMode);
      setImageModel(restoredRun.generationMode === 'precision' ? 'image-2.6-precision' : 'image-2.6-standard');
    }
    if (restoredRun.outputWidth && restoredRun.outputHeight) {
      setAspectRatio('custom');
      setCustomDimensions(`${restoredRun.outputWidth}×${restoredRun.outputHeight}`);
    } else {
      if (restoredRun.aspectRatio) setAspectRatio(restoredRun.aspectRatio);
      setCustomDimensions(restoredRun.customDimensions ?? '');
    }
  }, [restoredRun?.id, restoredRun?.prompt, restoredRun?.imageModel, restoredRun?.resolution,
    restoredRun?.quality, restoredRun?.aspectRatio, restoredRun?.generationMode, restoredRun?.customDimensions,
    restoredRun?.outputWidth, restoredRun?.outputHeight]);

  useEffect(() => {
    if (restoredTurns) setConversationTurns(restoredTurns);
    else if (restoredRun) setConversationTurns([{ id: restoredRun.id, prompt: restoredRun.prompt, resultId: restoredRun.id }]);
  }, [restoredTurns, restoredRun?.id]);

  const selectCategory = (id: string) => {
    setCategoryId(id);
    setSkillId(null);
    setPrompt('');
    setPromptId(null);
    setRightsConfirmed(false);
    setNonOfficialTemplate(false);
    setLocalError(null);
    setPromptEnhancementError(null);
  };

  const selectSkill = (selected: DesignStudioSkill) => {
    setSkillId(selected.id);
    setPrompt('');
    setPromptId(null);
    setRightsConfirmed(false);
    setNonOfficialTemplate(false);
    setLocalError(null);
    setPromptEnhancementError(null);
    textareaRef.current?.focus();
  };

  const updateReferenceFiles = (files: File[]) => {
    const boundedFiles = files.slice(0, MAX_REFERENCE_FILES);
    referenceFilesRef.current = boundedFiles;
    setReferenceFiles(boundedFiles);
    onReferenceFilesChange?.(boundedFiles);
  };

  const appendReferenceFiles = (files: File[]) => {
    const current = referenceFilesRef.current;
    if (current.length + files.length > MAX_REFERENCE_FILES) {
      setLocalError(`参考图最多可添加 ${MAX_REFERENCE_FILES} 张。`);
      return;
    }
    const invalidFile = files.find((file) => !['image/png', 'image/jpeg', 'image/webp', 'image/avif'].includes(file.type)
      || !file.size || file.size >= 20 * 1024 * 1024);
    if (invalidFile) {
      setLocalError('参考图仅支持 PNG、JPEG、WEBP、AVIF，每张必须小于 20 MB。');
      return;
    }
    const existing = new Set(current.map((file) => `${file.name}:${file.size}:${file.type}:${file.lastModified}`));
    const additions = files.filter((file) => {
      const key = `${file.name}:${file.size}:${file.type}:${file.lastModified}`;
      if (existing.has(key)) return false;
      existing.add(key);
      return true;
    });
    updateReferenceFiles([...current, ...additions]);
  };

  const importGeneratedReferences = async (sources: GeneratedReferenceSource[]) => {
    if (!sources.length || isImportingReferenceRef.current) return;
    const availableSlots = MAX_REFERENCE_FILES - referenceFilesRef.current.length;
    if (availableSlots <= 0) {
      setLocalError(`参考图最多可添加 ${MAX_REFERENCE_FILES} 张。`);
      return;
    }

    isImportingReferenceRef.current = true;
    const authEpoch = useWebConsumerAuthStore.getState().authEpoch;
    const restoredRunId = restoredRunIdRef.current;
    const stillCurrent = () => isMountedRef.current && restoredRunId === restoredRunIdRef.current
      && authEpoch === useWebConsumerAuthStore.getState().authEpoch;
    setIsImportingReference(true);
    setLocalError(null);
    try {
      const files = await Promise.all(sources.slice(0, availableSlots).map(async (source) => {
        const url = [generatedImageDownloadUrl(source.id, source.downloadUrl, source.url), source.url]
          .find((value): value is string => Boolean(value && isOwnedImageReferenceUrl(value)));
        if (!url) throw new Error('图片没有可读取的引用地址');
        return loadImageReferenceFile(url, `generated-image-${source.id.replace(/[^a-zA-Z0-9_-]/g, '-')}`);
      }));
      if (stillCurrent()) appendReferenceFiles(files);
    } catch {
      if (stillCurrent()) setLocalError('生成图片未能添加为参考图，请重试。');
    } finally {
      isImportingReferenceRef.current = false;
      if (isMountedRef.current) setIsImportingReference(false);
    }
  };

  const importTransferredReferences = (payload: ImageTransferPayload) => {
    if (busy || isImportingReferenceRef.current) return;
    if (!payload.files.length && !payload.urls.length) return;
    setLocalError(null);
    if (payload.files.length) {
      appendReferenceFiles(payload.files);
      return;
    }
    isImportingReferenceRef.current = true;
    const authEpoch = useWebConsumerAuthStore.getState().authEpoch;
    const restoredRunId = restoredRunIdRef.current;
    const stillCurrent = () => isMountedRef.current && restoredRunId === restoredRunIdRef.current
      && authEpoch === useWebConsumerAuthStore.getState().authEpoch;
    setIsImportingReference(true);
    void resolveImageTransfer(payload, { maxFiles: MAX_REFERENCE_FILES - referenceFilesRef.current.length })
      .then((files) => { if (stillCurrent()) appendReferenceFiles(files); })
      .catch((error: unknown) => {
        if (stillCurrent()) setLocalError(error instanceof Error ? error.message : '图片无法粘贴，请重新上传。');
      }).finally(() => {
        isImportingReferenceRef.current = false;
        if (isMountedRef.current) setIsImportingReference(false);
      });
  };

  const handleComposerPaste = (event: ClipboardEvent<HTMLFormElement>) => {
    if (busy || isImportingReferenceRef.current) return;
    const payload = imageTransferPayload(event.clipboardData);
    if (!payload.files.length && !payload.urls.length) return;
    event.preventDefault();
    importTransferredReferences(payload);
  };

  const findDraggedGeneratedImage = (dataTransfer: DataTransfer): GeneratedReferenceSource | null => {
    const draggedValue = dataTransfer.getData('text/uri-list') || dataTransfer.getData('text/plain');
    const draggedUrl = draggedValue
      .split(/\r?\n/)
      .map((line) => line.trim())
      .find((line) => line && !line.startsWith('#'));
    if (!draggedUrl) return null;
    const canonicalDraggedUrl = canonicalImageUrl(draggedUrl);
    return results
      .flatMap(generatedReferenceSources)
      .find((source) => [source.url, source.downloadUrl]
        .filter((url): url is string => Boolean(url))
        .some((url) => canonicalImageUrl(url) === canonicalDraggedUrl)) ?? null;
  };

  const handleComposerDrop = (event: DragEvent<HTMLFormElement>) => {
    if (busy || isImportingReferenceRef.current) return;
    const payload = imageTransferPayload(event.dataTransfer);
    const droppedFiles = payload.files;
    const generatedImage = droppedFiles.length ? null : findDraggedGeneratedImage(event.dataTransfer);
    if (!droppedFiles.length && !generatedImage && !payload.urls.length) return;

    event.preventDefault();
    event.stopPropagation();
    setLocalError(null);
    if (droppedFiles.length) {
      appendReferenceFiles(droppedFiles);
      return;
    }
    if (generatedImage) void importGeneratedReferences([generatedImage]);
    else importTransferredReferences(payload);
  };

  const enhanceStudioPrompt = async () => {
    const draftSnapshot = prompt;
    const content = draftSnapshot.trim();
    if (!content || busy) return;

    setPromptEnhancementError(null);
    setIsEnhancingPrompt(true);
    try {
      const enhanced = await enhancePrompt(content);
      if (!isMountedRef.current || latestPromptRef.current !== draftSnapshot) return;
      latestPromptRef.current = enhanced;
      setPrompt(enhanced);
      queueMicrotask(() => textareaRef.current?.focus());
    } catch {
      if (isMountedRef.current && latestPromptRef.current === draftSnapshot) {
        setPromptEnhancementError('提示词强化失败，请稍后重试。');
      }
    } finally {
      if (isMountedRef.current) setIsEnhancingPrompt(false);
    }
  };

  const buildDraft = (): DesignStudioDraft | null => {
    if (!prompt.trim() || ratioError || missingRequiredReferences) return null;
    if (requiresRightsConfirmation && !rightsConfirmed) return null;
    if (requiresNonOfficialTemplate && !nonOfficialTemplate) return null;
    return {
      categoryId: skill ? category?.id ?? FREEFORM_IMAGE_SKILL_ID : FREEFORM_IMAGE_SKILL_ID,
      skillId: effectiveSkill.id,
      kind: effectiveSkill.kind,
      prompt: prompt.trim(),
      promptId,
      referenceFiles,
      aspectRatio: effectiveSkill.kind === 'text' ? 'auto' : selection.aspectRatio,
      generationMode: effectiveSkill.kind === 'text' ? 'standard' : generationMode,
      ...(imageModels && effectiveSkill.kind !== 'text' ? {
        imageModel,
        resolution: selectedImageModel?.resolutions.length ? selectedParameters.resolution : null,
        quality: selectedImageModel?.qualities.length ? selectedParameters.quality : null,
        ...imageModelRequestOptions(selectedImageModel!, selection),
      } : {}),
      customDimensions: effectiveSkill.kind === 'text' || selectedImageModel ? '' : customDimensions,
      rightsConfirmed,
      nonOfficialTemplate,
    };
  };

  const submitDraft = async () => {
    const draft = buildDraft();
    if (!draft || busy) return;
    setLocalError(null);
    setIsSending(true);
    try {
      const submittedRunId = await onSubmit(draft);
      if (typeof submittedRunId !== 'string') return;
      setConversationTurns((current) => current.some((turn) => turn.id === submittedRunId) ? current : [...current, {
        id: submittedRunId,
        prompt: draft.prompt,
        resultId: submittedRunId,
      }]);
      setPrompt('');
      setPromptId(null);
      updateReferenceFiles([]);
      queueMicrotask(() => textareaRef.current?.focus());
    } catch (error) {
      setLocalError(error instanceof Error ? error.message : '提交失败，请稍后重试。');
    } finally {
      setIsSending(false);
    }
  };

  const renderComposer = (chatComposer: boolean) => (
    <form
      className={`design-studio-composer${chatComposer ? ' design-studio-chat-composer' : ''}`}
      onDragOverCapture={(event) => {
        const types = Array.from(event.dataTransfer.types ?? []);
        if (types.includes('Files') || types.includes('text/uri-list')) event.preventDefault();
      }}
      onDropCapture={handleComposerDrop}
      onPasteCapture={handleComposerPaste}
      onSubmit={(event) => {
        event.preventDefault();
        void submitDraft();
      }}
    >
      <div className="design-studio-composer-input">
        {skill && (
          <div className="design-studio-selected-skill">
            <span>{skill.title}</span>
            {!chatComposer && (
              <button
                type="button"
                aria-label={`移除技能 ${skill.title}`}
                disabled={busy}
                onClick={() => {
                  setSkillId(null);
                  setPrompt('');
                  setPromptId(null);
                  setLocalError(null);
                  setPromptEnhancementError(null);
                }}
              >×</button>
            )}
          </div>
        )}
        <label htmlFor="design-studio-prompt" className="design-studio-visually-hidden">描述你的创作需求</label>
        <textarea
          ref={textareaRef}
          id="design-studio-prompt"
          value={prompt}
          onChange={(event) => {
            setPrompt(event.target.value);
            setPromptEnhancementError(null);
          }}
          onKeyDown={(event) => {
            if (event.key !== 'Enter' || event.shiftKey || event.nativeEvent.isComposing) return;
            event.preventDefault();
            event.currentTarget.form?.requestSubmit(submitButtonRef.current ?? undefined);
          }}
          placeholder={chatComposer
            ? '继续描述你的修改或下一步想法'
            : skill ? '描述你想做什么，或选择上方提示词' : '直接描述想生成的图片；按钮和推荐文案均可选'}
          disabled={busy}
          rows={chatComposer ? 2 : 4}
        />
      </div>
      <div className="design-studio-composer-controls">
        <input
          ref={fileInputRef}
          className="design-studio-visually-hidden"
          type="file"
          accept="image/*"
          multiple
          aria-label="添加参考图片"
          disabled={busy}
          onChange={(event) => {
            const selected = Array.from(event.target.files ?? []).map((file) => normalizeImageFile(file) ?? file);
            appendReferenceFiles(selected);
            event.target.value = '';
          }}
        />
        <button
          className="design-studio-upload"
          type="button"
          aria-label="选择参考图片"
          title="添加参考图"
          disabled={busy || referenceFiles.length >= MAX_REFERENCE_FILES}
          onClick={() => fileInputRef.current?.click()}
        >＋</button>
        {imageModels && selectedImageModel ?
          <ImageRatioPicker ratio={selection.aspectRatio} customDimensions={customDimensions}
            route={ratioRoute} model={selectedImageModel} disabled={busy}
            onRatio={(ratio, dimensions) => {
              setAspectRatio(ratio); setCustomDimensions(dimensions);
            }} />
          : <ImageRatioPicker
            ratio={aspectRatio}
            customDimensions={customDimensions}
            route={ratioRoute}
            disabled={busy}
            onRatio={(ratio, dimensions) => { setAspectRatio(ratio); setCustomDimensions(dimensions); }}
          />}
        <ImageGenerationModePicker mode={generationMode} disabled={busy} onMode={setGenerationMode}
          imageModel={imageModel} imageModels={imageModels}
          onImageModel={imageModels ? (model) => {
            const nextModel = imageModels.find((option) => option.id === model);
            if (!nextModel?.available) return;
            const nextParameters = normalizeImageModelSelection(nextModel, selection);
            setModelParameters((current) => ({
              ...current,
              [model]: {
                resolution: nextParameters.resolution,
                quality: nextParameters.quality,
              },
            }));
            setAspectRatio(nextParameters.aspectRatio);
            setImageModel(model);
            setGenerationMode(imageModelGenerationMode(model));
          } : undefined} />
        {imageModels && selectedImageModel && <>
          {selectedImageModel.resolutions.length > 1 && <label className="design-studio-model-parameter">
            <span>分辨率</span>
            <select aria-label="图片分辨率" value={selectedParameters.resolution} disabled={busy}
              onChange={(event) => setModelParameters((current) => ({
                ...current, [imageModel]: {
                  resolution: event.target.value,
                  quality: selectedParameters.quality,
                },
              }))}>
              {selectedImageModel.resolutions.map((resolution) => (
                <option key={resolution} value={resolution}>
                  {resolution}
                </option>
              ))}
            </select>
          </label>}
          {selectedImageModel.qualities.length > 1 && <label className="design-studio-model-parameter">
            <span>画质</span>
            <select aria-label="图片画质" value={selectedParameters.quality} disabled={busy}
              onChange={(event) => setModelParameters((current) => ({
                ...current, [imageModel]: {
                  resolution: selectedParameters.resolution,
                  quality: event.target.value,
                },
              }))}>
              {selectedImageModel.qualities.map((quality) => (
                <option key={quality} value={quality}>
                  {imageModelQualityLabel(quality)}
                </option>
              ))}
            </select>
          </label>}
        </>}
        <button
          className={`design-studio-enhance${isEnhancingPrompt ? ' is-loading' : ''}`}
          type="button"
          aria-label="强化提示词"
          aria-busy={isEnhancingPrompt}
          title={prompt.trim() ? '让描述更清晰、更适合图片生成' : '输入创作描述后可强化提示词'}
          disabled={!prompt.trim() || busy}
          onClick={() => { void enhanceStudioPrompt(); }}
        >
          <span className="design-studio-enhance-icon" aria-hidden="true">
            {isEnhancingPrompt ? (
              <span className="design-studio-enhance-spinner" />
            ) : (
              <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                <path d="M12 3l1.45 4.55L18 9l-4.55 1.45L12 15l-1.45-4.55L6 9l4.55-1.45L12 3z" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M19 14l.8 2.2L22 17l-2.2.8L19 20l-.8-2.2L16 17l2.2-.8L19 14z" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            )}
          </span>
          <span aria-live="polite">{isEnhancingPrompt ? '强化中' : '强化'}</span>
        </button>
        <button
          ref={submitButtonRef}
          className="design-studio-submit"
          type="submit"
          aria-label={busy ? '正在提交' : chatComposer ? '发送消息' : '开始创作'}
          title={chatComposer ? '发送消息' : '开始创作'}
          disabled={!prompt.trim() || busy || Boolean(ratioError) || missingRequiredReferences
            || (requiresRightsConfirmation && !rightsConfirmed)
            || (requiresNonOfficialTemplate && !nonOfficialTemplate)}
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true"><path d="M8 2.5l-4.5 4.5h3V13h3V7h3L8 2.5z" /></svg>
        </button>
      </div>
      {missingRequiredReferences && skill?.referenceHint && (
        <p className="design-studio-reference-hint" role="status">{skill.referenceHint}</p>
      )}
      {ratioError && <p className="design-studio-error" role="alert">{ratioError}</p>}
      {imageModelsError && <p className="design-studio-error" role="alert">{imageModelsError}</p>}
      {promptEnhancementError && <p className="design-studio-error" role="alert">{promptEnhancementError}</p>}
      {referenceFiles.length > 0 && (
        <ul className="design-studio-reference-list" aria-label="已选参考图">
          {referenceFiles.map((file, index) => (
            <li key={`${file.name}-${file.lastModified}-${index}`}>
              <ImageAttachmentPreview file={file} label={file.name}>
              <span title={file.name}>{file.name}</span>
              <button
                type="button"
                aria-label={`移除参考图 ${file.name}`}
                disabled={busy}
                onClick={() => updateReferenceFiles(referenceFiles.filter((_, fileIndex) => fileIndex !== index))}
              >×</button>
              </ImageAttachmentPreview>
            </li>
          ))}
        </ul>
      )}
      {requiresRightsConfirmation && (
        <label className="design-studio-safety-check">
          <input type="checkbox" checked={rightsConfirmed} disabled={busy} onChange={(event) => setRightsConfirmed(event.target.checked)} />
          <span>我确认有权编辑上传的素材。</span>
        </label>
      )}
      {requiresNonOfficialTemplate && (
        <label className="design-studio-safety-check">
          <input type="checkbox" checked={nonOfficialTemplate} disabled={busy} onChange={(event) => setNonOfficialTemplate(event.target.checked)} />
          <span>我确认这不是第三方签发的正式证明，而是我有权编辑的设计模板。</span>
        </label>
      )}
    </form>
  );

  return (
    <main className={`design-studio${conversationTurns.length ? ' is-conversation' : ''}`} data-testid="design-studio">
      <div className="design-studio-inner">
        {conversationTurns.length === 0 && (
          <>
            <header className="design-studio-heading">
              <h1>说出你的想法，让作品一步步成形。</h1>
            </header>

            {categories.length === 0 ? (
              <section className="design-studio-empty" role="status">当前没有可用的创作技能，请稍后再试。</section>
            ) : (
              <>
            <div className="design-studio-tabs" role="tablist" aria-label="创作分类">
              {categories.map((item) => (
                <button
                  key={item.id}
                  id={`${tabIdPrefix}-tab-${item.id}`}
                  type="button"
                  role="tab"
                  aria-selected={category?.id === item.id}
                  aria-controls={`${tabIdPrefix}-panel`}
                  tabIndex={category?.id === item.id ? 0 : -1}
                  className={category?.id === item.id ? 'is-selected' : ''}
                  disabled={busy}
                  onClick={() => selectCategory(item.id)}
                  onKeyDown={(event) => {
                    const currentIndex = categories.findIndex((candidate) => candidate.id === item.id);
                    const lastIndex = categories.length - 1;
                    const nextIndex = event.key === 'ArrowRight' || event.key === 'ArrowDown'
                      ? (currentIndex + 1) % categories.length
                      : event.key === 'ArrowLeft' || event.key === 'ArrowUp'
                        ? (currentIndex - 1 + categories.length) % categories.length
                        : event.key === 'Home' ? 0 : event.key === 'End' ? lastIndex : -1;
                    if (nextIndex < 0) return;
                    event.preventDefault();
                    const next = categories[nextIndex];
                    selectCategory(next.id);
                    document.getElementById(`${tabIdPrefix}-tab-${next.id}`)?.focus();
                  }}
                >
                  <span className="design-studio-tab-icon" aria-hidden="true">{categoryGlyphs[item.id] ?? '◈'}</span>
                  {item.title}
                </button>
              ))}
            </div>

            <section
              id={`${tabIdPrefix}-panel`}
              className="design-studio-workspace"
              role="tabpanel"
              aria-labelledby={`${tabIdPrefix}-tab-${category?.id}`}
            >
              {skill ? (
                <div className="design-studio-prompts" aria-label={`${skill.title}提示词`}>
                  {skill.prompts.map((item) => (
                    <button
                      key={item.id}
                      type="button"
                      disabled={busy}
                      onClick={() => {
                        setPrompt(item.text);
                        setPromptId(item.id);
                        setPromptEnhancementError(null);
                        textareaRef.current?.focus();
                      }}
                    >
                      {item.title}<span aria-hidden="true">↘</span>
                    </button>
                  ))}
                </div>
              ) : (
                <div className="design-studio-skills" aria-label={`${category?.title ?? ''}技能`}>
                  {category?.skills.map((item) => (
                    <button
                      key={item.id}
                      type="button"
                      className="design-studio-skill"
                      aria-pressed={false}
                      disabled={busy}
                      onClick={() => selectSkill(item)}
                    >
                      <span className="design-studio-skill-icon" data-skill-icon={item.id} aria-hidden="true">
                        {skillGlyphs[item.id] ?? fallbackSkillGlyphs[item.kind]}
                      </span>
                      <span>{item.title}</span>
                    </button>
                  ))}
                </div>
              )}

              {renderComposer(false)}
              {(localError || submitError) && <p className="design-studio-error" role="alert">{localError || submitError}</p>}
            </section>
              </>
            )}
          </>
        )}

        {conversationTurns.length > 0 && (
          <section className="design-studio-chat-shell" aria-label="创作会话">
            <div className="design-studio-chat-stream" role="log" aria-label="创作对话" aria-live="polite">
              {conversationTurns.map((turn) => {
                const result = turn.resultId ? results.find((item) => item.id === turn.resultId) : undefined;
                const pending = !result || result.status === 'queued' || result.status === 'running';
                return (
                  <div key={turn.id} className="design-studio-chat-turn">
                    <div className="houmao-user-message-row" data-message-id={`${turn.id}-user`}>
                      <div className="houmao-user-message-stack">
                        <div className="houmao-user-message">{turn.prompt}</div>
                      </div>
                    </div>
                    {pending ? (
                      <>
                        {result?.warning && <p className="design-studio-reference-hint" role="status">{result.warning}</p>}
                        <TypingIndicator
                          variant="houmao"
                          assistantAvatarUrl="/images/houmao-icon-blue-cut.png"
                          label={result?.kind === 'text' ? '思考中'
                            : ['正在生成图片…', imageCanvasProgressLabel(result?.canvasProgress)].filter(Boolean).join(' ')}
                        />
                      </>
                    ) : (
                      <>
                        <AssistantMessage
                          message={resultMessage(result, turn.prompt)}
                          variant="houmao"
                          assistantAvatarUrl="/images/houmao-icon-blue-cut.png"
                          generatedImagePresentation="gallery"
                          showTextActions={result.status !== 'failed'}
                          onQuote={() => {
                            void importGeneratedReferences(generatedReferenceSources(result));
                            setPrompt('请基于上一个结果继续调整：');
                            setPromptEnhancementError(null);
                            queueMicrotask(() => textareaRef.current?.focus());
                          }}
                        />
                        {(result.downloadUrl || (result.status === 'failed' && onRetry)) && (
                          <div className="design-studio-chat-result-actions assistant-message-actions" aria-label="创作结果操作">
                            {result.downloadUrl && (
                              <a href={result.downloadUrl} download aria-label="下载作品">
                                <svg viewBox="0 0 24 24" aria-hidden="true">
                                  <path d="M12 3v12m0 0 5-5m-5 5-5-5M5 20h14" />
                                </svg>
                                <span>下载作品</span>
                              </a>
                            )}
                            {result.status === 'failed' && onRetry && (
                              <button type="button" onClick={() => onRetry(result.id)} aria-label="重试创作">
                                <svg viewBox="0 0 24 24" aria-hidden="true">
                                  <path d="M20 6v5h-5M4 18v-5h5M6.1 9a7 7 0 0 1 11.5-2.6L20 9M4 15l2.4 2.6A7 7 0 0 0 17.9 15" />
                                </svg>
                                <span>重试</span>
                              </button>
                            )}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                );
              })}
              <div ref={chatEndRef} />
            </div>
            <div className="design-studio-chat-input-dock">
              {renderComposer(true)}
              {(localError || submitError) && <p className="design-studio-error" role="alert">{localError || submitError}</p>}
            </div>
          </section>
        )}
      </div>

    </main>
  );
}
